import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, Home, Building2, Calendar } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { api } from "../services/apiClient";

const DevelopmentsPage = () => {
  const navigate = useNavigate();
  const [developments, setDevelopments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDevelopments();
  }, []);

  const fetchDevelopments = async () => {
    try {
      const response = await api.get("/developments", { auth: false });
      const data = await response.json();
      setDevelopments(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching developments:", error);
    } finally {
      setLoading(false);
    }
  };

  const statusColor = (status) => {
    switch (status) {
      case "En Planeación":
        return "bg-amber-100 text-amber-800";
      case "En Construcción":
        return "bg-blue-100 text-blue-800";
      case "Entregado":
        return "bg-emerald-100 text-emerald-800";
      default:
        return "bg-muted text-muted-foreground";
    }
  };

  return (
    <div className="min-h-screen">
      <Header />

      <div className="bg-gradient-to-br from-primary/10 to-secondary/10 py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <h1
            className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 tracking-tight"
            style={{ fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}
            data-testid="developments-title"
          >
            Desarrollos Inmobiliarios
          </h1>
          <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
            Descubre proyectos completos con propiedades vinculadas
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 md:py-16">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : developments.length === 0 ? (
          <div className="text-center py-16">
            <Building2 className="w-16 h-16 text-muted-foreground/40 mx-auto mb-4" />
            <h3 className="text-xl font-bold mb-2">
              No hay desarrollos disponibles
            </h3>
            <p className="text-muted-foreground">
              Los desarrollos aparecerán aquí cuando se publiquen.
            </p>
          </div>
        ) : (
          <div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            data-testid="developments-grid"
          >
            {developments.map((dev) => (
              <div
                key={dev.id}
                onClick={() => navigate(`/development/${dev.id}`)}
                className="bg-background border border-border/40 rounded-lg overflow-hidden hover:-translate-y-1 hover:shadow-xl hover:border-primary/40 transition-all duration-300 cursor-pointer"
                data-testid={`development-card-${dev.id}`}
              >
                <div className="aspect-video overflow-hidden bg-muted">
                  <img
                    src={
                      dev.cover_image_url ||
                      dev.image_urls?.[0] ||
                      "https://images.pexels.com/photos/323780/pexels-photo-323780.jpeg"
                    }
                    alt={dev.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-5">
                  <div className="flex items-center justify-between mb-2">
                    <span
                      className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${statusColor(dev.status)}`}
                    >
                      {dev.status || "En Construcción"}
                    </span>
                    {dev.development_type && (
                      <span className="text-xs text-muted-foreground">
                        {dev.development_type}
                      </span>
                    )}
                  </div>
                  <h3
                    className="text-lg font-bold mb-1 line-clamp-1"
                    style={{ fontFamily: "Manrope, sans-serif" }}
                  >
                    {dev.name}
                  </h3>
                  {dev.address && (
                    <p className="text-xs text-muted-foreground mb-3 flex items-center line-clamp-1">
                      <MapPin className="w-3 h-3 mr-1 flex-shrink-0" />
                      {dev.address}
                    </p>
                  )}
                  {dev.description && (
                    <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                      {dev.description}
                    </p>
                  )}
                  <div className="flex items-center justify-between pt-3 border-t border-border/40">
                    <div className="flex items-center gap-1 text-sm text-primary font-medium">
                      <Home className="w-4 h-4" />
                      {dev.property_count || 0} propiedades
                    </div>
                    {dev.estimated_delivery && (
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Calendar className="w-3 h-3" />
                        {dev.estimated_delivery}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default DevelopmentsPage;
